import { cn } from "cn";
import { useRef } from "react";
import { Button } from "@/components/ui/button/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BwMoreFill } from "@/components/ui/icons";
import { OVERLAY_BUTTON_CLASS_NAME } from "./attachment-overlay-button";

/** One item of a tile's "…" menu. */
export type AttachmentAction = {
  key: string;
  label: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  onSelect?: () => void;
  /** Rendered as a download link instead of a button. */
  href?: string;
  /** Red, and always listed last, after a separator. */
  destructive?: boolean;
};

/**
 * The top-right "…" of a tile. Where a pointer can hover, the trigger hides until the
 * tile is hovered or reached with the keyboard, and stays while its menu is open, so a
 * wall of files is not a wall of buttons. On a touch screen it always shows.
 */
export function AttachmentMenu({
  fileName,
  actions,
  className,
}: {
  fileName: string;
  actions: AttachmentAction[];
  className?: string;
}) {
  // Picking an item closes the menu and Radix hands focus back to the trigger — focus
  // Chrome counts as :focus-visible, which would keep the button on after the pointer
  // leaves. A pick made with the pointer waives that return; keyboard picks keep it.
  const pickedWithPointer = useRef(false);
  const regularActions = actions.filter((action) => !action.destructive);
  const destructiveActions = actions.filter((action) => action.destructive);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          kind="outline"
          variant="neutral"
          size="icon-md"
          aria-label={`Options for ${fileName}`}
          data-test="attachment-item-menu-button"
          onClick={(event) => event.stopPropagation()}
          // `relative` anchors the overlay's `after` layer to the button. A tile
          // lays the menu over its picture with `absolute` (className, last wins);
          // a list row leaves it in the flow, where a static button would let
          // that layer fill the nearest positioned ancestor.
          className={cn(
            /* sorted */
            "relative",
            "pointer-fine:opacity-0",
            "transition-opacity",
            "group-hover/attachment:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100",
            OVERLAY_BUTTON_CLASS_NAME,
            className,
          )}
        >
          <BwMoreFill size={20} />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-48"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={() => {
          pickedWithPointer.current = true;
        }}
        onKeyDown={() => {
          pickedWithPointer.current = false;
        }}
        onCloseAutoFocus={(event) => {
          if (!pickedWithPointer.current) return;
          pickedWithPointer.current = false;
          event.preventDefault();
        }}
      >
        {regularActions.map((action) => (
          <AttachmentMenuItem key={action.key} action={action} />
        ))}
        {regularActions.length > 0 && destructiveActions.length > 0 && (
          <DropdownMenuSeparator />
        )}
        {destructiveActions.map((action) => (
          <AttachmentMenuItem key={action.key} action={action} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AttachmentMenuItem({ action }: { action: AttachmentAction }) {
  const Icon = action.icon;
  const body = (
    <>
      {Icon && (
        <Icon
          size={20}
          className={cn(
            /* sorted */
            "shrink-0",
            !action.destructive && "text-text-dark-high",
          )}
        />
      )}
      <span
        className={cn(
          /* sorted */
          "grow",
          "text-sm",
          !action.destructive && "text-text-dark-max",
        )}
      >
        {action.label}
      </span>
    </>
  );
  const variant = action.destructive ? "destructive" : undefined;

  if (action.href) {
    return (
      <DropdownMenuItem asChild variant={variant} className="flex gap-2">
        <a href={action.href} download target="_self" rel="noreferrer">
          {body}
        </a>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      variant={variant}
      className="flex gap-2"
      onSelect={action.onSelect}
    >
      {body}
    </DropdownMenuItem>
  );
}
